// News-Tab — Live-News mit Filter-Pills (ALL / CRYPTO / GEO / MACRO) +
// per-Item EVE-Button.
//
// Datenquelle: fullData.newsApi.articles (Crypto/Forex Cron im Worker,
// alle 5min) + fullData.newsApiGeo.articles (Geo-Politik). Kategorisierung
// per Keyword-Heuristik aus Titel + Quelle. Sortierung nach „vor X min/h"
// (neueste oben), Top-30 sichtbar.
//
// EVE-Button: ruft direkt den Drawer-Store (askEve) ohne Such-Modus
// damit EVE die Frage direkt bekommt — kein Prop-Pipe mehr (Anti-Drift).

import { useMemo, useState } from 'react';
import { ExternalLink, Zap } from 'lucide-react';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { useEveDrawerStore } from '@/stores/eve-drawer-store';
import type { NewsArticle } from '@/types/market';

type NewsCategory = 'ALL' | 'CRYPTO' | 'GEO' | 'MACRO';

interface NewsItem {
  id: string;
  title: string;
  source: string;
  ago: string;
  url: string | null;
  category: Exclude<NewsCategory, 'ALL'>;
}

export function TabNews() {
  const fullData = useMarketStore(selectFullData);
  const askEve = useEveDrawerStore((s) => s.askEve);
  const [category, setCategory] = useState<NewsCategory>('ALL');

  const items = useMemo<ReadonlyArray<NewsItem>>(() => {
    const crypto = (fullData?.newsApi?.articles ?? [])
      .map((a, i) => toItem(a, `c-${i}`, classifyArticle(a, 'CRYPTO')))
      .filter((x): x is NewsItem => x !== null);
    const geo = (fullData?.newsApiGeo?.articles ?? [])
      .map((a, i) => toItem(a, `g-${i}`, classifyArticle(a, 'GEO')))
      .filter((x): x is NewsItem => x !== null);
    return [...crypto, ...geo]
      .sort((a, b) => parseAgoMinutes(a.ago) - parseAgoMinutes(b.ago))
      .slice(0, 30);
  }, [fullData]);

  const filtered = category === 'ALL' ? items : items.filter((i) => i.category === category);
  const counts: Record<NewsCategory, number> = {
    ALL: items.length,
    CRYPTO: items.filter((i) => i.category === 'CRYPTO').length,
    GEO: items.filter((i) => i.category === 'GEO').length,
    MACRO: items.filter((i) => i.category === 'MACRO').length,
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <span className="text-[11px] text-neutral-500">
          {items.length === 0 ? 'Lädt…' : `${items.length} aktuelle Items`}
        </span>
        <div className="flex flex-wrap gap-1 text-[11px]">
          {(['ALL', 'CRYPTO', 'GEO', 'MACRO'] as const).map((c) => {
            const isActive = category === c;
            return (
              <button
                key={c}
                type="button"
                onClick={() => setCategory(c)}
                className={
                  isActive
                    ? 'rounded-full border border-amber-500/30 bg-amber-500/15 px-1.5 py-0.5 text-amber-300'
                    : 'rounded-full border border-white/10 px-1.5 py-0.5 text-neutral-400 hover:bg-white/5'
                }
              >
                {emojiForCategory(c)}
                <span className="ml-0.5 opacity-60">{counts[c]}</span>
              </button>
            );
          })}
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rounded-lg border border-white/5 p-3 text-center text-xs text-neutral-500">
          {items.length === 0 ? 'News werden geladen…' : 'Keine News in dieser Kategorie.'}
        </div>
      ) : (
        filtered.map((item) => {
          const titleClickable = item.url !== null;
          const titleClass = 'text-xs leading-snug text-neutral-200';
          return (
            <div
              key={item.id}
              className="glass rounded-lg border-l-2 border-amber-500/40 p-2.5"
            >
              <div className="flex items-start gap-1.5">
                <Zap className="mt-0.5 size-3 shrink-0 text-amber-400" />
                <div className="flex-1">
                  {titleClickable ? (
                    <a
                      href={item.url ?? '#'}
                      target="_blank"
                      rel="noreferrer noopener"
                      className={`${titleClass} block hover:text-amber-200 hover:underline`}
                    >
                      {item.title}
                    </a>
                  ) : (
                    <div className={titleClass}>{item.title}</div>
                  )}
                  <div className="mt-1.5 flex items-center justify-between">
                    <span className="text-[11px] text-neutral-500">
                      {item.source} · {item.ago}
                    </span>
                    <div className="flex gap-1">
                      {titleClickable ? (
                        <a
                          href={item.url ?? '#'}
                          target="_blank"
                          rel="noreferrer noopener"
                          className="inline-flex items-center gap-0.5 rounded border border-white/10 px-1.5 py-0.5 text-[11px] text-neutral-400 hover:text-neutral-100"
                          aria-label="Original-Artikel öffnen"
                        >
                          <ExternalLink className="size-2.5" />
                          Link
                        </a>
                      ) : null}
                      <button
                        type="button"
                        onClick={() =>
                          askEve(
                            `${item.title}\nanalysiere und recherchiere die Fakten und schau dir die gesamte marktlage an.`,
                            { displayText: item.title },
                          )
                        }
                        className="rounded border border-amber-500/40 bg-amber-500/10 px-1.5 py-0.5 text-[11px] text-amber-300 hover:bg-amber-500/20"
                      >
                        EVE
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })
      )}
    </div>
  );
}

function emojiForCategory(c: NewsCategory): string {
  switch (c) {
    case 'ALL':
      return 'ALL';
    case 'CRYPTO':
      return '⚡';
    case 'GEO':
      return '🌐';
    case 'MACRO':
      return '∼';
  }
}

// ── Article → NewsItem Mapping ──────────────────────────────────────────────

const MACRO_KEYWORDS = [
  'fed',
  'fomc',
  'powell',
  'cpi',
  'inflation',
  'jobs',
  'unemployment',
  'gdp',
  'ecb',
  'boj',
  'rate',
  'yield',
  'treasury',
];

function classifyArticle(
  a: NewsArticle,
  defaultCategory: 'CRYPTO' | 'GEO',
): Exclude<NewsCategory, 'ALL'> {
  const text = `${a.title ?? ''} ${a.description ?? ''}`.toLowerCase();
  if (MACRO_KEYWORDS.some((kw) => text.includes(kw))) return 'MACRO';
  return defaultCategory;
}

function toItem(
  a: NewsArticle,
  id: string,
  category: Exclude<NewsCategory, 'ALL'>,
): NewsItem | null {
  const title = a.title?.trim();
  if (!title) return null;
  const source = a.source?.name ?? 'News';
  const rawUrl = typeof a['url'] === 'string' ? (a['url'] as string).trim() : '';
  const url = rawUrl.length > 0 && /^https?:\/\//i.test(rawUrl) ? rawUrl : null;
  return {
    id,
    title,
    source,
    ago: formatAgo(a.publishedAt),
    url,
    category,
  };
}

function formatAgo(publishedAt: string | undefined): string {
  if (!publishedAt) return '—';
  const then = Date.parse(publishedAt);
  if (!Number.isFinite(then)) return '—';
  const diffSec = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (diffSec < 60) return `${diffSec}s`;
  const m = Math.floor(diffSec / 60);
  if (m < 60) return `${m}m`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.floor(h / 24);
  return `${d}d`;
}

/** Parsed „Xs/Xm/Xh/Xd" zurück in Minuten — für Sort (kleiner = neuer). */
function parseAgoMinutes(ago: string): number {
  const match = ago.match(/^(\d+)([smhd])$/);
  if (!match || !match[1] || !match[2]) return Number.MAX_SAFE_INTEGER;
  const n = parseInt(match[1], 10);
  switch (match[2]) {
    case 's':
      return n / 60;
    case 'm':
      return n;
    case 'h':
      return n * 60;
    case 'd':
      return n * 60 * 24;
    default:
      return Number.MAX_SAFE_INTEGER;
  }
}
